import { useGameStore } from '../store/game-store.ts';
import { EmblemBadge } from './founding/Emblem.tsx';
import { APP_NAME } from '../brand.ts';
import { postOnboarding } from '../api/onboarding.ts';
import { BrandMark } from './BrandMark.tsx';
import { AudioControl } from './AudioControl.tsx';

const TABS = [
  { id: 'galaxy' as const, label: 'Galaxis', icon: '🌌', available: true },
  { id: 'empire' as const, label: 'Gazdaság', icon: '⚙️', available: true },
  { id: 'research' as const, label: 'Kutatás', icon: '🔬', available: true },
  { id: 'construction' as const, label: 'Építés', icon: '🏗️', available: true },
  { id: 'fleets' as const, label: 'Flották', icon: '🚀', available: true },
  { id: 'diplomacy' as const, label: 'Diplomácia', icon: '🤝', available: true },
  { id: 'trade' as const, label: 'Kereskedelem', icon: '📦', available: true },
  { id: 'community' as const, label: 'Közösség', icon: '🏆', available: true },
] as const;

export function TopNav() {
  const { activeTab, setActiveTab, me, galaxyMap, liveBattle, setGuideOpen } = useGameStore();

  return (
    <nav aria-label="Főmenü" className="sci-fi-panel relative z-30 border-b border-slate-700/50 flex items-center px-2 sm:px-4 h-12 gap-1 flex-shrink-0">
      {/* z-30: the bar's blur makes it its own stacking context, so without it the popovers inside (hang / grafika beállítások) were painted UNDER the page content */}
      {/* Logo */}
      <div className="hidden sm:flex items-center gap-2 mr-1 sm:mr-4 flex-shrink-0">
        <BrandMark size={24} />
        <span className="hidden lg:inline font-mono font-semibold text-sm tracking-widest text-accent uppercase">
          {APP_NAME}
        </span>
      </div>

      {/* Navigáció */}
      {/* Phones: icon tabs (the label stays the accessible name); the strip scrolls rather than clips */}
      <div className="scroll-hint-x flex items-center gap-0.5 sm:gap-1 flex-1 min-w-0 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={(e) => { if (!tab.available) return; setActiveTab(tab.id); e.currentTarget.scrollIntoView?.({ inline: 'center', block: 'nearest', behavior: 'smooth' }); }}
            className={`nav-btn flex-shrink-0 min-h-[44px] min-w-[44px] justify-center ${activeTab === tab.id ? 'active' : ''} ${!tab.available ? 'opacity-40 cursor-not-allowed hidden sm:inline-flex' : ''}`}
            disabled={!tab.available}
            title={tab.available ? tab.label : 'Fejlesztés alatt'}
            aria-label={tab.label}
            aria-current={activeTab === tab.id ? 'page' : undefined}
          >
            <span aria-hidden className="xl:hidden text-lg leading-none">{tab.icon}</span>
            <span className="hidden xl:inline">{tab.label}</span>
            {!tab.available && (
              <span className="hidden md:inline ml-1 wip-badge py-0">WIP</span>
            )}
          </button>
        ))}
      </div>

      {/* LIVE csata értesítő */}
      {liveBattle && (
        <div data-testid="live-battle" aria-label="Élő csata" className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono font-bold"
          style={{ border: '1px solid rgba(239,68,68,0.5)', background: 'rgba(239,68,68,0.08)', color: '#f87171' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="hidden sm:inline">LIVE</span>
        </div>
      )}

      {/* Jobb oldal: játékos info */}
      <div className="flex items-center gap-3 text-xs text-slate-400 flex-shrink-0">
        {galaxyMap && (
          <span className="hidden lg:block font-mono">
            {galaxyMap.systemCount} rendszer
          </span>
        )}
        <AudioControl />
        {me?.civilization && (
          // M10.4: the first-steps guide can be reopened any time.
          // Phones and tablets: no room in the bar (it covered the tabs) — reopen from "Fiókom" instead
          <button type="button" className="nav-btn hidden lg:inline-flex min-h-[44px] min-w-[44px] justify-center" aria-label="Kezdő útmutató" title="Kezdő útmutató"
            onClick={() => { void postOnboarding('reopen').catch(() => undefined); setGuideOpen(true); }}>
            <span aria-hidden className="text-base leading-none">🧭</span>
          </button>
        )}
        {me && (
          // M10.6: bug report / suggestion. Only on wide screens: at tablet width the extra button pushed
          // the tab strip off screen (M10.6.1) — phones and tablets reach it from "Fiókom"
          <button type="button" className="nav-btn hidden lg:inline-flex min-h-[44px] min-w-[44px] justify-center" aria-label="Visszajelzés / hibajelentés" title="Visszajelzés / hibajelentés"
            onClick={() => useGameStore.getState().setFeedbackOpen(true)}>
            <span aria-hidden className="text-base leading-none">💬</span>
          </button>
        )}
        {me && (
          // M10.8: public changelog (wide screens; phones and tablets reach it from "Fiókom")
          <a href="/changelog" target="_blank" rel="noopener" className="nav-btn hidden lg:inline-flex min-h-[44px] min-w-[44px] justify-center items-center" aria-label="Újdonságok" title="Újdonságok / Változásnapló" data-testid="nav-changelog">
            <span aria-hidden className="text-base leading-none">📰</span>
          </a>
        )}
        {me && (
          // M10.2: account screen (profile, password, sessions, sign-out)
          <button
            type="button"
            onClick={() => setActiveTab('account')}
            className={`nav-btn flex items-center gap-2 min-h-[44px] min-w-[44px] justify-center ${activeTab === 'account' ? 'active' : ''}`}
            aria-label="Fiókom"
            aria-current={activeTab === 'account' ? 'page' : undefined}
            title="Fiókom"
          >
            {me.civilization
              ? <EmblemBadge id={me.civilization.emblem ?? 'nova'} color={me.civilization.color} size={26} />
              : (
                <span aria-hidden className="w-6 h-6 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center text-accent text-xs font-bold">
                  {me.player.username.slice(0, 1).toUpperCase()}
                </span>
              )}
            <span className="hidden lg:block">{me.player.username}</span>
          </button>
        )}
      </div>
    </nav>
  );
}
